import { UserInterface } from '@techniek-team/common';
import { ROLE_HIERARCHY } from '@techniek-team/oauth';
import { PermissionService } from './service/permission.service';
import { TtPermissionConfig } from './tt-permission.config';

/**
 * This module is intended to give a permission service similar to the Symfony
 * security component voter system.
 *
 * It comes with a PermissionService containing the methods isGranted and isDenied
 * to which you supply a {@see BasePermission} class (voter), a subject and any optional data
 * that you might need in your {@see BasePermission} class.
 *
 * This module also supplies a {@see IsGrantedDirective} for use within your html templates.
 * For information about the forRoot config options {@see TtPermissionConfig}
 *
 * @example
 * ```typescript
 * @NgModule({
 *   declarations: [
 *     AppComponent
 *   ],
 *   imports: [
 *     TtPermissionModule.forRoot({
 *       roleHierarchy: {
 *         ROLE_SKOLEO_TT_PLANNER_ADMIN: [
 *           'admin',
 *           'ROLE_SKOLEO_TT_PLANNER_LOCATION_MANAGEMENT',
 *           'ROLE_SKOLEO_TT_PLANNER_SCHEDULING_TEAM',
 *         ],
 *         ROLE_SKOLEO_TT_PLANNER_LOCATION_MANAGEMENT: 'locationManager',
 *         ROLE_SKOLEO_TT_PLANNER_SCHEDULING_TEAM: 'schedulingTeam',
 *       },
 *       userService: UserService,
 *     }),
 *   ],
 *   bootstrap: [AppComponent],
 * })
 * export class AppModule {}
 * ```
 */
export function provideTtPermissions<T extends UserInterface>(options: TtPermissionConfig<T>) {
  //eslint-disable-next-line @typescript-eslint/no-misused-spread
  options = { ...new TtPermissionConfig(), ...options };

  return [
    { provide: TtPermissionConfig, useValue: options },
    PermissionService,
    { provide: ROLE_HIERARCHY, useValue: options.roleHierarchy ?? {}, multi: true },
  ];
}
